import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {LoginDto, ResponseError} from "../../../api_client";
import {AuthClientId, BasePathUrl} from "../../../appsettings";
import {useApi} from "../../../hooks/useApi";
import {State} from "../../../redux/store";
import {authStore} from "../data";
import {ILoadingState, IRestore} from "../data/authStore";
import {getAllPermissions} from "../../../hooks/useUserPermissions";
import {t} from "i18next";
import {
  eventsBroadcastChannel,
  Events,
  type ImpersonatePayload
} from "../../../utils/eventsBroadcastChannel";
import {
  postImpersonate,
  postImpersonateLogout
} from "../../../utils/impersonation";
import {
  getSpaHomeUrl,
  parseReturnUrl,
  resolveImpersonateRedirect
} from "../../../utils/returnUrl";
import {useMemo} from "react";

const getLogoutUrl = (): string =>
  new URL(`${BasePathUrl}/ui-api/auth/logout`, globalThis.location.origin).toString();


export const useAuthorization = () => {
  const {authApi, usersApi} = useApi();
  const {actions} = authStore;
  const dispatch = useDispatch();

  const beginLoading = (type: keyof ILoadingState) => {
    dispatch(actions.setError(undefined));
    dispatch(actions.setLoading({type: type, isLoading: true}));
  };

  const endLoading = (type: keyof ILoadingState) => {
    dispatch(actions.setLoading({type: type, isLoading: false}));
  };

  const setError = (err: any) => {
    dispatch(actions.setError(err));
  };

  const setRestore = (restore: IRestore) => {
    dispatch(actions.setRestore(restore));
  };

  const clearError = () => {
    dispatch(actions.setError(undefined));
  };

  const getUserClaims = async () => {
    const userClaims = await authApi.uiApiAuthUserPermissionsGet();
    dispatch(actions.setUserClaims(userClaims));
    if (getAllPermissions(userClaims).moduleAccess) {
      dispatch(actions.setIsAuth(true));
    } else {
      setError({
        code: "USER_PERMISSION_ERR",
        message: t("messages.userAccessIsNotAllowed")
      });
      dispatch(actions.setIsAuth(false));
    }
  };

  const getUserInfo = async () => {
    const user = await usersApi.uiApiUsersCurrentGet();

    dispatch(actions.setUser(user));
  };

  const signIn = async (model: LoginDto) => {
    try {
      beginLoading("signIn");
      await authApi.uiApiAuthLoginPost({loginDto: model});
      const returnUrl = parseReturnUrl();
      if (returnUrl) {
        globalThis.location.replace(returnUrl);
      } else {
        await getUserClaims();
        await getUserInfo();
      }
    } catch (error) {
      if (error instanceof ResponseError) {
        error = await error.response.json();
      }
      setError(error);
    } finally {
      endLoading("signIn");
    }
  };

  const signOut = (): void => {
    beginLoading("signOut");
    eventsBroadcastChannel.schedule(Events.logout);
    globalThis.location.href = getLogoutUrl();
  };

  /**
   * Вход администратора в модуль под другим пользователем.
   * POST /Account/Impersonate/{id} → 200 Set-Cookie + { redirectUrl },
   * затем schedule и location.href на домашний маршрут SPA (#/users).
   * Без access=accounts PrivateRoute сам отправит на /login (LoginSessionPanel).
   */
  const impersonateUser = async (
    targetUserId: string,
    targetUserName?: string | null
  ): Promise<void> => {
    const adminUserId = user?.id;
    const adminUserName = user?.userName;

    if (!adminUserId || !adminUserName) {
      throw new Error(t("impersonate.adminNotResolved"));
    }

    const payload: ImpersonatePayload = {
      adminUserId,
      adminUserName,
      impersonatedUserId: targetUserId,
      impersonatedUserName: targetUserName ?? targetUserId
    };

    beginLoading("impersonateUser");

    try {
      const result = await postImpersonate(targetUserId, getSpaHomeUrl());
      debugger;
      eventsBroadcastChannel.schedule(Events.impersonate, payload);
      globalThis.location.href = resolveImpersonateRedirect(result.redirectUrl);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Impersonate user error:", error);
      endLoading("impersonateUser");
      throw error;
    }
  };

  /**
   * Выход из-под пользователя: POST Impersonate/Logout → schedule → #/users.
   * PostLogoutRedirectUri с сервера не используем (это URI обычного logout → login).
   * Schedule только после 200 (как в Layouts endImpersonation).
   */
  const endImpersonation = async (): Promise<void> => {
    beginLoading("endImpersonation");
    try {
      await postImpersonateLogout(AuthClientId);
      eventsBroadcastChannel.schedule(Events.impersonateEnd);
      globalThis.location.href = getSpaHomeUrl();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("End impersonation error:", error);
      endLoading("endImpersonation");
      throw error;
    }
  };

  const error = useSelector((state: State) => state.auth.error, shallowEqual);
  const restore = useSelector((state: State) => state.auth.restore, shallowEqual);
  const loading = useSelector((state: State) => state.auth.loading, shallowEqual);
  const user = useSelector((state: State) => state.auth.data.user, shallowEqual);

  const isImpersonating = useMemo<boolean>(() =>
    Boolean(user?.impersonatingUserLogin && user.impersonatingUserLogin !== "-"),
    [user?.impersonatingUserLogin]
  );

  return {
    isImpersonating,
    signIn,
    signOut,
    setRestore,
    clearError,
    error,
    loading,
    restore,
    user,
    impersonateUser,
    endImpersonation
  };
};
